import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminUserListItem } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { differenceInCalendarDays } from 'date-fns'
import { useState } from 'react'
import { useAdminUsersQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/users/')({
  component: AdminUsersPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

function formatDaysAgo(date: Date): string {
  const days = differenceInCalendarDays(new Date(), date)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return `${days} days ago`
}

const columns: TableColumn<AdminUserListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (user) => (
      <CellLink to="/admin/users/$id" params={{ id: user.id }} className="font-mono text-xs">
        {user.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Name',
    render: (user) => <CellPrimary>{user.name || '-'}</CellPrimary>,
  },
  {
    header: 'Email',
    render: (user) => <CellText>{user.email}</CellText>,
  },
  {
    header: 'Role',
    width: '80px',
    render: (user) => (
      <Badge variant={user.role === 'ADMIN' ? 'destructive' : 'secondary'} size="sm">
        {user.role}
      </Badge>
    ),
  },
  {
    header: 'Type',
    width: '100px',
    render: (user) => <CellText>{user.userType ?? '-'}</CellText>,
  },
  {
    header: 'Onboarding',
    width: '110px',
    render: (user) => (
      <Badge variant={user.onboardingCompleted ? 'success' : 'secondary'} size="sm">
        {user.onboardingCompleted ? 'Done' : 'Pending'}
      </Badge>
    ),
  },
  {
    header: 'Last Active',
    width: '130px',
    render: (user) => (
      <CellText>
        {user.lastAccessedAt ? formatDaysAgo(new Date(user.lastAccessedAt)) : 'Never'}
      </CellText>
    ),
  },
  {
    header: 'Created',
    width: '130px',
    render: (user) => <CellText>{formatFullDate(user.createdAt)}</CellText>,
  },
]

function AdminUsersPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const usersQuery = useAdminUsersQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const users = usersQuery.data?.data?.items ?? []
  const total = usersQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search users..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={users}
        isLoading={usersQuery.isLoading}
        emptyMessage="No users found."
        keyExtractor={(u) => u.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
